import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Image as ImageIcon,
  Video,
  Paperclip,
  ChevronDown,
  ChevronRight,
  ArrowLeft,
  AlertCircle,
  Loader2,
  Check,
} from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";
import { useCreateCase, useUpdateCase } from "../hooks/use-cases";
import { presignDocumentUpload } from "../api/cases-api";
import { uploadCaseMedia, formatMediaUrl } from "../api/media-api";
import { putToStorage } from "../../../lib/uploads";
import InlineDocumentViewer from "./InlineDocumentViewer";
import MediaPreviewModal from "./MediaPreviewModal";
import { resolveMimeType } from "../utils/documentStore";
import { isCoFounder } from "../../../utils/cofounderBadge";

const SPECIES_OPTIONS = [
  "Canine",
  "Feline",
  "Equine",
  "Bovine",
  "Avian",
  "Exotic",
];

const MAX_IMAGES = 4;
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;  // 10 MB
const MAX_VIDEO_SIZE = 200 * 1024 * 1024; // 200 MB
const MAX_DOC_SIZE = 20 * 1024 * 1024;    // 20 MB
const MAX_TITLE_LENGTH = 120;
const MAX_CONTENT_LENGTH = 3000;

const getAvatar = (name) =>
  (name || "U")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

// ─── Custom Dropdown (matches ShareCase custom select style) ──────────────────
const CustomDropdown = ({
  options,
  value,
  onChange,
  placeholder = "Select",
  dropUp = false,
  align = "left",
  className = "",
  buttonClassName = "",
  menuClassName = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
    };
  }, []);

  // Compute label from value
  const currentLabel = React.useMemo(() => {
    if (value === "" || value === null || value === undefined) return null;
    const found = options.find((opt) => (typeof opt === "string" ? opt === value : opt.value === value));
    if (!found) return value;
    return typeof found === "string" ? found : found.label;
  }, [options, value]);

  return (
    <div className={`relative inline-block ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between gap-1.5 px-2.5 sm:px-3 py-1.5 bg-[#F5F6F8] hover:bg-[#eceef2] active:bg-[#e2e5eb] border border-gray-200 rounded-lg text-xs font-medium transition-all text-left ${isOpen
            ? "bg-white border-indigo-500 ring-2 ring-indigo-100 shadow-sm text-gray-900"
            : "text-gray-700"
          } ${buttonClassName}`}
      >
        <span className={currentLabel ? "text-gray-900 font-semibold" : "text-gray-500"}>
          {currentLabel || placeholder}
        </span>
        <ChevronDown
          size={13}
          className={`text-gray-500 transition-transform duration-200 shrink-0 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div
          className={`absolute z-50 min-w-[110px] bg-white border border-gray-200 rounded-xl shadow-[0_12px_30px_-6px_rgba(0,0,0,0.14)] p-1 max-h-56 overflow-y-auto ${dropUp ? "bottom-full mb-1.5" : "top-full mt-1.5"
            } ${align === "right" ? "right-0" : "left-0"} ${menuClassName}`}
        >
          {options.map((opt) => {
            const optValue = typeof opt === "string" ? opt : opt.value;
            const optLabel = typeof opt === "string" ? opt : opt.label;
            const isSelected = value === optValue;
            return (
              <button
                key={optValue}
                type="button"
                onClick={() => {
                  if (isSelected && placeholder === "Species") {
                    onChange("");
                  } else {
                    onChange(optValue);
                  }
                  setIsOpen(false);
                }}
                className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs transition-colors text-left ${isSelected
                    ? "bg-indigo-50 text-indigo-900 font-semibold"
                    : "text-gray-700 hover:bg-[#F5F6F8] font-medium"
                  }`}
              >
                <span>{optLabel}</span>
                {isSelected && <Check size={13} className="text-indigo-600 shrink-0" strokeWidth={2.5} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Extract clean permanent key from a media item
const getCleanMediaKey = (m) => {
  if (m.mediaKey) return m.mediaKey;
  if (!m.url) return null;
  const match = m.url.match(/(?:public|tmp)\/case\/(?:document|media)\/[^?]+/);
  return match ? match[0] : m.url;
};

// ── Normalise existing CaseMedia rows for edit mode ──────────────────────────
const normaliseExistingMedia = (media = []) => {
  const imgs = [];
  let vid = null;
  let doc = null;
  for (const m of media) {
    const key = getCleanMediaKey(m);
    const previewUrl = formatMediaUrl(m.url);
    if (m.type === "IMAGE") {
      imgs.push({ id: m.id, file: null, previewUrl, key, uploading: false, progress: 100, filename: m.filename, mimeType: m.mimeType, sizeBytes: m.sizeBytes });
    } else if (m.type === "VIDEO") {
      vid = { id: m.id, file: null, previewUrl, key, uploading: false, progress: 100, filename: m.filename, mimeType: m.mimeType, sizeBytes: m.sizeBytes };
    } else if (m.type === "DOCUMENT") {
      doc = { id: m.id, file: null, previewUrl, url: previewUrl, key, uploading: false, filename: m.filename, mimeType: m.mimeType, size: m.sizeBytes };
    }
  }
  return { imgs, vid, doc };
};

// ── Main Composer Component ──────────────────────────────────────────────────
const QuickDiscussionComposer = ({
  onBack,
  onClose,
  onSuccess,
  editMode = false,
  caseId = null,
  initialData = null,
}) => {
  const { user } = useAuth();
  const createCase = useCreateCase();
  const updateCase = useUpdateCase();

  // Pre-populate state from initialData when in editMode
  const initMedia = React.useMemo(() => {
    if (editMode && initialData?.media) return normaliseExistingMedia(initialData.media);
    return { imgs: [], vid: null, doc: null };
  }, [editMode, initialData]);

  const [mode, setMode] = useState("compose"); // "compose" | "preview"
  const [title, setTitle] = useState(() => (editMode && initialData?.title) ? initialData.title : "");
  const [text, setText] = useState(() => (editMode && initialData?.content) ? initialData.content : "");
  const [species, setSpecies] = useState(() => (editMode && initialData?.species) ? initialData.species : "");
  const [isRestricted, setIsRestricted] = useState(() =>
    editMode ? Boolean(initialData?.isRestricted || initialData?.visibility === "Private") : false
  );
  const [images, setImages] = useState(initMedia.imgs);
  const [video, setVideo] = useState(initMedia.vid);
  const [reportFile, setReportFile] = useState(initMedia.doc);
  const [error, setError] = useState(null);
  const [posting, setPosting] = useState(false);
  const [lightboxMedia, setLightboxMedia] = useState(null);
  const [hoveredTooltip, setHoveredTooltip] = useState(null); // 'image' | 'video' | 'report' | null

  const imageInputRef = useRef();
  const videoInputRef = useRef();
  const docInputRef = useRef();
  const textareaRef = useRef(null);
  const titleRef = useRef(null);

  // Track active blob URLs so they can be revoked on unmount (prevents memory leaks with large files)
  const blobUrlsRef = useRef([]);
  useEffect(() => {
    const urls = [
      ...images.map((i) => i.previewUrl),
      video?.previewUrl,
      reportFile?.previewUrl,
    ].filter((u) => u?.startsWith("blob:"));
    blobUrlsRef.current = urls;
  }, [images, video, reportFile]);
  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  // ── Auto-resize logic for title and description textareas ──
  const adjustHeights = useCallback(() => {
    if (titleRef.current) {
      titleRef.current.style.height = "auto";
      titleRef.current.style.height = `${Math.max(titleRef.current.scrollHeight, 42)}px`;
    }
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.max(textareaRef.current.scrollHeight + 8, 130)}px`;
    }
  }, []);

  // Multi-stage auto-resize so both initial mount, edit mode, font load, and state updates expand to 100% of content
  useEffect(() => {
    adjustHeights();
    const rAF = requestAnimationFrame(adjustHeights);
    const t1 = setTimeout(adjustHeights, 40);
    const t2 = setTimeout(adjustHeights, 150);
    return () => {
      cancelAnimationFrame(rAF);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [title, text, mode, editMode, initialData, adjustHeights]);

  // Sync initialData when in editMode in case props load asynchronously
  useEffect(() => {
    if (editMode && initialData) {
      if (initialData.title !== undefined) setTitle(initialData.title || "");
      if (initialData.content !== undefined) setText(initialData.content || "");
      if (initialData.species !== undefined) setSpecies(initialData.species || "");
      if (initialData.isRestricted !== undefined || initialData.visibility !== undefined) {
        setIsRestricted(Boolean(initialData.isRestricted || initialData.visibility === "Private"));
      }
      if (initialData.media) {
        const norm = normaliseExistingMedia(initialData.media);
        setImages(norm.imgs);
        setVideo(norm.vid);
        setReportFile(norm.doc);
      }
    }
  }, [editMode, initialData]);

  // ── Upload helpers ────────────────────────────────────────────
  const uploadFile = useCallback(async (file, section, onProgress) => {
    const result = await uploadCaseMedia(file, section, onProgress);
    return result.url; // staging key (tmp/…)
  }, []);

  const uploadDocument = useCallback(async (file) => {
    const resolvedMime = resolveMimeType(file);
    const data = await presignDocumentUpload({
      filename: file.name,
      contentType: resolvedMime,
      size: file.size,
    });
    const { key, uploadUrl } = data.data;
    await putToStorage(uploadUrl, file);
    return key;
  }, []);

  // ── Image selection ───────────────────────────────────────────
  const handleImages = async (e) => {
    const rawFiles = Array.from(e.target.files || []);
    if (!rawFiles.length) return;
    e.target.value = "";

    // Proactive size check before starting uploads
    const oversized = rawFiles.find((f) => f.size > MAX_IMAGE_SIZE);
    if (oversized) {
      setError("Image file is too large. Images must be under 10 MB.");
      return;
    }

    const files = rawFiles.slice(0, MAX_IMAGES - images.length);
    if (!files.length) return;
    setError(null);

    const newEntries = files.map((f) => ({
      file: f,
      previewUrl: URL.createObjectURL(f),
      key: null,
      uploading: true,
      progress: 0,
      filename: f.name,
      mimeType: f.type,
      sizeBytes: f.size,
    }));
    setImages((prev) => [...prev, ...newEntries]);

    for (const entry of newEntries) {
      try {
        const key = await uploadFile(entry.file, "GENERAL", (p) => {
          setImages((prev) =>
            prev.map((img) => (img.previewUrl === entry.previewUrl ? { ...img, progress: p } : img))
          );
        });
        setImages((prev) =>
          prev.map((img) => (img.previewUrl === entry.previewUrl ? { ...img, key, uploading: false } : img))
        );
      } catch (err) {
        setImages((prev) => prev.filter((img) => img.previewUrl !== entry.previewUrl));
        const serverMsg = err?.response?.data?.message || "";
        if (serverMsg.toLowerCase().includes("large") || serverMsg.toLowerCase().includes("exceed") || serverMsg.toLowerCase().includes("size")) {
          setError("Image file is too large. Images must be under 10 MB.");
        } else {
          setError("Failed to upload image. Please check your connection and try again.");
        }
      }
    }
  };

  // ── Video selection ───────────────────────────────────────────
  const handleVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    if (file.size > MAX_VIDEO_SIZE) {
      setError("Video file is too large. Videos must be under 200 MB.");
      return;
    }

    setError(null);
    const entry = {
      file,
      previewUrl: URL.createObjectURL(file),
      key: null,
      uploading: true,
      progress: 0,
      filename: file.name,
      mimeType: file.type,
      sizeBytes: file.size,
    };
    setVideo(entry);

    try {
      const key = await uploadFile(file, "GENERAL", (p) =>
        setVideo((prev) => (prev ? { ...prev, progress: p } : prev))
      );
      setVideo((prev) => (prev ? { ...prev, key, uploading: false } : null));
    } catch (err) {
      setVideo(null);
      const serverMsg = err?.response?.data?.message || "";
      if (serverMsg.toLowerCase().includes("large") || serverMsg.toLowerCase().includes("exceed") || serverMsg.toLowerCase().includes("size")) {
        setError("Video file is too large. Videos must be under 200 MB.");
      } else {
        setError("Failed to upload video. Please check your connection and try again.");
      }
    }
  };

  // ── Document selection ────────────────────────────────────────
  const handleDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    if (file.size > MAX_DOC_SIZE) {
      setError("Document file is too large. Reports must be under 20 MB.");
      return;
    }

    setError(null);
    const resolvedMime = resolveMimeType(file);
    const entry = {
      file,
      filename: file.name,
      size: file.size,
      mimeType: resolvedMime,
      previewUrl: URL.createObjectURL(file),
      key: null,
      uploading: true,
    };
    setReportFile(entry);

    try {
      const key = await uploadDocument(file);
      setReportFile((prev) => (prev ? { ...prev, key, uploading: false } : null));
    } catch (err) {
      setReportFile(null);
      const serverMsg = err?.response?.data?.message || "";
      if (serverMsg.toLowerCase().includes("large") || serverMsg.toLowerCase().includes("exceed") || serverMsg.toLowerCase().includes("size")) {
        setError("Document file is too large. Reports must be under 20 MB.");
      } else {
        setError("Failed to upload document. Please check your connection and try again.");
      }
    }
  };

  // ── Step Navigation & Validation ─────────────────────────────
  const anyUploading = images.some((i) => i.uploading) || video?.uploading || reportFile?.uploading;
  const hasContent = text.trim(); // text body is always required; media is optional on top of it

  const handleGoToPreview = () => {
    if (!title.trim()) {
      setError("Please enter a discussion title.");
      return;
    }
    if (!hasContent) {
      setError("Please write a description.");
      return;
    }
    if (anyUploading) {
      setError("Please wait for your attachments to finish uploading.");
      return;
    }

    setError(null);
    setMode("preview");
  };

  // ── Post / Publish Case ──────────────────────────────────────
  const handlePost = async () => {
    if (!title.trim()) {
      setError("Please enter a discussion title.");
      return;
    }
    if (!hasContent) {
      setError("Please write a description.");
      return;
    }
    if (anyUploading) {
      setError("Please wait for your attachments to finish uploading.");
      return;
    }

    setPosting(true);
    setError(null);

    try {
      const media = [
        ...images.filter((i) => i.key).map((i, idx) => ({
          url: i.key,
          section: "GENERAL",
          filename: i.filename,
          mimeType: i.mimeType,
          sizeBytes: i.sizeBytes,
          order: idx,
        })),
        ...(video?.key
          ? [
            {
              url: video.key,
              section: "GENERAL",
              filename: video.filename,
              mimeType: video.mimeType,
              sizeBytes: video.sizeBytes,
              order: images.length,
            },
          ]
          : []),
        ...(reportFile?.key
          ? [
            {
              url: reportFile.key,
              section: "GENERAL",
              filename: reportFile.filename,
              mimeType: reportFile.mimeType,
              sizeBytes: reportFile.size,
              order: images.length + (video?.key ? 1 : 0),
            },
          ]
          : []),
      ];

      if (editMode && caseId) {
        await updateCase.mutateAsync({
          caseId,
          data: {
            title: title.trim(),
            content: text.trim(),
            species: species || null,
            isRestricted,
            media,
            tags: initialData?.tags || [],
            discussionType: null,
          },
        });
      } else {
        await createCase.mutateAsync({
          postType: "quick_discussion",
          discussionType: null,
          content: text.trim(),
          tags: [],
          species: species || null,
          isRestricted,
          media,
          title: title.trim(),
          keyLearnings: [],
        });
      }

      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    } catch (err) {
      const serverMsg = err?.response?.data?.message;
      if (serverMsg && typeof serverMsg === "string") {
        if (serverMsg.toLowerCase().includes("character") || serverMsg.toLowerCase().includes("too long")) {
          setError("Description is too long. Please keep it under 3,000 characters.");
        } else if (serverMsg.toLowerCase().includes("title")) {
          setError("Please provide a valid discussion title.");
        } else if (serverMsg.toLowerCase().includes("media") || serverMsg.toLowerCase().includes("storage") || serverMsg.toLowerCase().includes("upload")) {
          setError("There was a problem with the attached file. Please re-upload and try again.");
        } else if (serverMsg.toLowerCase().includes("forbidden") || serverMsg.toLowerCase().includes("permission")) {
          setError("You do not have permission to edit this post.");
        } else {
          setError(serverMsg);
        }
      } else {
        setError("Could not publish discussion. Please try again.");
      }
    } finally {
      setPosting(false);
    }
  };

  // ── PREVIEW MODE RENDER ──────────────────────────────────────
  if (mode === "preview") {
    return (
      <div className="w-full max-w-4xl mx-auto py-4 px-3 sm:px-6 pb-28 animate-fadeIn">
        {/* Top Bar Navigation */}
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setMode("compose")}
            className="flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={16} /> Edit Discussion
          </button>

          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-3 py-1 bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs font-semibold rounded-full">
              Quick Discussion
            </span>
            {species && (
              <span className="px-3 py-1 bg-pink-50 text-pink-700 border border-pink-200 text-xs font-semibold rounded-full">
                {species}
              </span>
            )}
            <span
              className={`px-3 py-1 text-xs font-semibold rounded-full border ${isRestricted
                  ? "bg-amber-50 text-amber-700 border-amber-200"
                  : "bg-emerald-50 text-emerald-700 border-emerald-200"
                }`}
            >
              {isRestricted ? "Private" : "Public"}
            </span>
            <span className="px-3 py-1 bg-purple-50 text-purple-700 border border-purple-200 text-xs font-semibold rounded-full">
              Preview Mode
            </span>
          </div>
        </div>

        {/* Main Presentation Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8 space-y-6 min-w-0 overflow-hidden">
          {/* Header / Author */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-bold flex items-center justify-center text-lg overflow-hidden border border-indigo-200 shrink-0">
              {user?.avatarUrl ? (
                <img src={user.avatarUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                getAvatar(user?.name)
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-bold text-gray-900 text-base">
                   {user?.name ? (isCoFounder(user) ? user.name : (user.name.replace(/^Dr\.?\s*/i, "") ? `Dr. ${user.name.replace(/^Dr\.?\s*/i, "")}` : "Dr. Veterinarian")) : "Dr. Veterinarian"}
                </h3>
                <span className="bg-indigo-50 text-indigo-700 px-3 py-0.5 rounded-full text-xs font-semibold border border-indigo-100">
                  {user?.jobRole || user?.specialization || (user?.role === "student" ? "Student" : "Veterinarian")}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500 mt-1 flex-wrap">
                <span>Just now</span>
                <span>•</span>
                {species && (
                  <>
                    <span className="bg-pink-50 text-pink-700 px-2.5 py-0.5 rounded-full font-medium text-xs border border-pink-100">
                      {species}
                    </span>
                    <span>•</span>
                  </>
                )}
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${isRestricted
                    ? "bg-amber-50 text-amber-700 border-amber-200"
                    : "bg-emerald-50 text-emerald-700 border-emerald-200"
                  }`}>
                  {isRestricted ? "Private" : "Public"}
                </span>
              </div>
            </div>
          </div>

          {/* Title */}
          {title && <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-snug break-words [overflow-wrap:anywhere]">{title}</h1>}

          {/* Text Content */}
          {text && (
            <p className="text-gray-800 text-sm sm:text-base whitespace-pre-wrap leading-relaxed break-words [overflow-wrap:anywhere]">
              {text}
            </p>
          )}

          {/* Attached Media Section: Document -> Video -> Images (Matches View Case sequence) */}
          {(reportFile || video || images.length > 0) && (
            <div className="space-y-4 pt-2 border-t border-gray-100">
              {/* 1. Document Attachments */}
              {reportFile && (
                <InlineDocumentViewer
                  url={reportFile.previewUrl || reportFile.url}
                  mediaId={reportFile.id}
                  filename={reportFile.filename}
                  mimeType={reportFile.mimeType}
                  sizeBytes={reportFile.size}
                />
              )}

              {/* 2. Video Attachments */}
              {video && (
                <div className="rounded-xl overflow-hidden bg-black max-h-[420px] flex items-center justify-center">
                  <video
                    src={video.previewUrl}
                    controls
                    preload="metadata"
                    playsInline
                    className="w-full max-h-[400px] object-contain rounded-xl"
                  />
                </div>
              )}

              {/* 3. Image Gallery */}
              {images.length > 0 && (
                <div
                  className={`grid gap-3 rounded-xl overflow-hidden ${images.length === 1 ? "grid-cols-1" : "grid-cols-2"
                    }`}
                >
                  {images.map((img, i) => (
                    <div
                      key={i}
                      onClick={() => setLightboxMedia(img)}
                      className="relative group cursor-pointer overflow-hidden rounded-xl bg-gray-100 border border-gray-200 aspect-[4/3]"
                    >
                      <img
                        src={img.previewUrl}
                        alt={img.filename || "Post attachment"}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <span className="opacity-0 group-hover:opacity-100 bg-white/90 text-gray-900 text-xs font-semibold px-2.5 py-1 rounded-md shadow-xs transition-opacity">
                          View
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mt-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3 shrink-0 shadow-sm font-medium">
            <AlertCircle size={16} className="shrink-0" /> {error}
          </div>
        )}

        {/* Bottom Action Bar */}
        <div className="mt-6 w-full bg-white rounded-2xl border border-gray-200 p-4 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <span className="text-xs sm:text-sm font-medium text-gray-600">
            {editMode ? "Ready to save your changes?" : "Looks good? Publish to share with the community."}
          </span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handlePost}
              disabled={posting}
              className="px-6 py-2.5 text-xs sm:text-sm font-bold text-white bg-[#6C63FF] hover:bg-[#5850E6] rounded-xl transition-colors disabled:opacity-60 flex items-center gap-2 shadow-md"
            >
              {posting ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  {editMode ? "Saving…" : "Publishing…"}
                </>
              ) : (
                <>{editMode ? "✓ Save Changes" : "✓ Post Discussion"}</>
              )}
            </button>
          </div>
        </div>

        {/* Lightbox Modal */}
        <MediaPreviewModal mediaItem={lightboxMedia} onClose={() => setLightboxMedia(null)} />
      </div>
    );
  }

  // ── COMPOSE MODE RENDER ──────────────────────────────────────
  if (user?.role === 'student') {
    return (
      <div className="w-full max-w-xl mx-auto flex flex-col items-center justify-center">
        <div className="relative bg-white rounded-2xl shadow-xl w-full flex flex-col p-8 text-center gap-3">
          <div className="text-4xl mb-2">🩺</div>
          <h2 className="font-bold text-gray-900">Posting not available</h2>
          <p className="text-sm text-gray-500">Only verified veterinarians can publish quick discussions.</p>
          <button
            type="button"
            onClick={onClose || onBack}
            className="mt-2 text-sm font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-xl mx-auto flex flex-col items-start my-auto">
      {/* Top Back Link Outside Card */}
      <button
        type="button"
        onClick={() => {
          if (editMode) {
            if (onClose) onClose();
          } else {
            if (onBack) onBack();
          }
        }}
        className="flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-gray-900 mb-2 sm:mb-3 transition-colors ml-1 shrink-0"
      >
        <ArrowLeft size={16} /> {editMode ? "Cancel Edit" : "Back"}
      </button>

      <div className="relative bg-white rounded-2xl shadow-xl w-full flex flex-col">
        {/* Header */}
        <div className="flex items-center px-5 py-4 border-b border-gray-100 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-bold flex items-center justify-center text-sm overflow-hidden border border-gray-200 shrink-0">
              {user?.avatarUrl ? (
                <img src={user.avatarUrl} alt={user?.name || "Doctor"} className="w-full h-full object-cover" />
              ) : (
                getAvatar(user?.name)
              )}
            </div>
            <div className="flex flex-col">
              <h2 className="font-bold text-gray-900 text-sm sm:text-base leading-tight">
                {user?.name ? (isCoFounder(user) ? user.name : (user.name.startsWith("Dr.") ? user.name : `Dr. ${user.name}`)) : "Dr. Veterinarian"}
              </h2>
              <p className="text-xs text-[#5B6B82] font-medium leading-tight mt-0.5">
                {user?.jobRole || user?.specialization || (user?.role === "student" ? "Student" : "Veterinary Surgeon")}
              </p>
            </div>
          </div>
        </div>

        {/* Body Form */}
        <div className="p-5 space-y-4">
          {/* Title input */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Discussion Title <span className="text-red-500">*</span>
              </label>
              <span
                className={`text-xs font-medium transition-colors ${title.length >= MAX_TITLE_LENGTH
                    ? "text-red-500 font-bold"
                    : title.length >= MAX_TITLE_LENGTH * 0.9
                      ? "text-amber-500 font-semibold"
                      : "text-gray-400"
                  }`}
              >
                {title.length}/{MAX_TITLE_LENGTH}
              </span>
            </div>
            <textarea
              ref={titleRef}
              rows={1}
              value={title}
              onChange={(e) => {
                const cleaned = e.target.value.replace(/[\r\n]+/g, " ");
                setTitle(cleaned);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  textareaRef.current?.focus();
                }
              }}
              placeholder="Add a title…"
              maxLength={MAX_TITLE_LENGTH}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 resize-none overflow-hidden leading-relaxed break-words [overflow-wrap:anywhere] transition-[height] duration-75"
              style={{ minHeight: "42px" }}
            />
          </div>

          {/* Textarea */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setError(null);
              }}
              onInput={adjustHeights}
              onFocus={adjustHeights}
              placeholder="What's happening? Share a clinical observation, question, or insight..."
              rows={5}
              maxLength={MAX_CONTENT_LENGTH}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 resize-none overflow-hidden leading-relaxed break-words [overflow-wrap:anywhere]"
              style={{ minHeight: "130px" }}
            />
            <div className="flex justify-end mt-1.5">
              <span
                className={`text-xs font-medium transition-colors ${text.length >= MAX_CONTENT_LENGTH
                    ? "text-red-500 font-bold"
                    : text.length >= MAX_CONTENT_LENGTH * 0.9
                      ? "text-amber-500 font-semibold"
                      : "text-gray-400"
                  }`}
              >
                {text.length}/{MAX_CONTENT_LENGTH}
              </span>
            </div>
          </div>

          {/* Image previews */}
          {images.length > 0 && (
            <div className={`grid gap-2 ${images.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
              {images.map((img, i) => (
                <div key={i} className="relative rounded-xl overflow-hidden h-32 bg-gray-100 border border-gray-200">
                  <img src={img.previewUrl} alt="" className="w-full h-full object-cover" />
                  {img.uploading && (
                    <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white">
                      <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin mb-1" />
                      <span className="text-xs">{img.progress ?? 0}%</span>
                    </div>
                  )}
                  {!img.uploading && (
                    <button
                      type="button"
                      onClick={() => setImages((prev) => prev.filter((_, idx) => idx !== i))}
                      className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 text-white text-xs flex items-center justify-center hover:bg-black/80 shadow-xs"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Video preview */}
          {video && (
            <div className="relative rounded-2xl overflow-hidden bg-black border border-gray-200 max-h-[340px] flex items-center justify-center group shadow-sm">
              <video
                src={video.previewUrl}
                controls={!video.uploading}
                preload="metadata"
                playsInline
                className="w-full max-h-[320px] object-contain rounded-2xl"
              />

              {/* Filename Badge */}
              <div className="absolute top-2.5 left-2.5 bg-black/60 backdrop-blur-md text-white text-xs px-2.5 py-1 rounded-lg flex items-center gap-1.5 max-w-[calc(100%-80px)] truncate font-medium pointer-events-none z-10">
                <Video size={13} className="text-purple-400 shrink-0" />
                <span className="truncate">{video.filename}</span>
              </div>

              {/* Remove Button */}
              {!video.uploading && (
                <button
                  type="button"
                  onClick={() => setVideo(null)}
                  className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-black/60 hover:bg-red-600 text-white text-xs flex items-center justify-center backdrop-blur-md transition-all shadow-md z-10 hover:scale-105 active:scale-95"
                  title="Remove video"
                >
                  ✕
                </button>
              )}

              {/* Uploading Overlay */}
              {video.uploading && (
                <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center text-white backdrop-blur-xs z-20">
                  <div className="w-8 h-8 border-2 border-white/30 border-t-indigo-400 rounded-full animate-spin mb-2" />
                  <span className="text-xs font-semibold">Uploading video… {video.progress ?? 0}%</span>
                </div>
              )}
            </div>
          )}

          {/* Document preview */}
          {reportFile && (
            <InlineDocumentViewer
              url={reportFile.previewUrl || reportFile.url}
              mediaId={reportFile.id}
              filename={reportFile.filename}
              mimeType={reportFile.mimeType}
              sizeBytes={reportFile.size}
              uploading={reportFile.uploading}
              onRemove={() => setReportFile(null)}
            />
          )}

          {/* Error banner */}
          {error && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
              <AlertCircle size={16} className="shrink-0" />
              {error}
            </div>
          )}
        </div>

        {/* Toolbar + Footer */}
        <div className="border-t border-gray-100 px-4 sm:px-5 py-3 bg-white rounded-b-2xl">
          {/* Media / Dropdowns Toolbar */}
          {/* On desktop: Image · Video · Report · Species ── [flex spacer] ── Public/Private */}
          {/* On mobile:  Image · Video · Report · Species (wraps gracefully within card, Public stays in footer) */}
          <div className="flex items-center gap-2 mb-3">
            {/* Left: media buttons + species — flex-wrap enabled so all stay inside card on small screens */}
            <div className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0 flex-wrap sm:flex-nowrap">
              {/* Image Upload Button with Tooltip */}
              <div className="relative inline-block shrink-0">
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  onMouseEnter={() => setHoveredTooltip("image")}
                  onMouseLeave={() => setHoveredTooltip(null)}
                  disabled={images.length >= MAX_IMAGES}
                  className={`flex items-center gap-1.5 px-2 sm:px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${images.length > 0
                      ? "bg-indigo-50/50 text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                      : "border-gray-200 text-gray-600 hover:border-indigo-300 hover:text-indigo-600 bg-white"
                    } disabled:opacity-40 disabled:cursor-not-allowed`}
                >
                  <ImageIcon size={14} className={images.length > 0 ? "text-indigo-600" : "text-gray-500"} />
                  <span>Image</span>
                  {images.length > 0 && (
                    <span className="text-[10px] bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded-full font-bold leading-none">
                      {images.length}/{MAX_IMAGES}
                    </span>
                  )}
                </button>

                {hoveredTooltip === "image" && (
                  <div className="absolute left-0 bottom-full mb-2 z-50 w-52 p-2.5 bg-gray-900 text-white rounded-xl shadow-2xl text-xs space-y-1.5 border border-gray-700/60 pointer-events-none animate-in fade-in zoom-in-95">
                    <div className="flex items-center gap-1.5 font-bold text-indigo-300 text-xs border-b border-gray-700 pb-1">
                      <ImageIcon size={13} />
                      <span>Allowed Image Formats</span>
                    </div>
                    <div className="space-y-1 text-[11px] text-gray-200 pt-0.5">
                      <p><span className="font-semibold text-white">• Supported: </span>JPG, JPEG, PNG, WebP</p>
                      <p><span className="font-semibold text-white">• Limit: </span>Max {MAX_IMAGES} images</p>
                      <p><span className="font-semibold text-white">• Max Size: </span>10 MB per image</p>
                    </div>
                    <div className="mt-1 pt-1 border-t border-gray-700 text-gray-300 text-[10px] font-medium flex items-center justify-between">
                      <span>Attached: {images.length}/{MAX_IMAGES}</span>
                      {images.length >= MAX_IMAGES && (
                        <span className="text-amber-400 font-semibold">Max reached</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <input
                ref={imageInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                multiple
                className="hidden"
                onChange={handleImages}
              />

              {/* Video Upload Button with Tooltip */}
              <div className="relative inline-block shrink-0">
                <button
                  type="button"
                  onClick={() => videoInputRef.current?.click()}
                  onMouseEnter={() => setHoveredTooltip("video")}
                  onMouseLeave={() => setHoveredTooltip(null)}
                  disabled={!!video}
                  className={`flex items-center gap-1.5 px-2 sm:px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${video
                      ? "bg-amber-50/70 text-amber-800 border-amber-300 hover:bg-amber-100"
                      : "border-gray-200 text-gray-600 hover:border-indigo-300 hover:text-indigo-600 bg-white"
                    } disabled:opacity-40 disabled:cursor-not-allowed`}
                >
                  <Video size={14} className={video ? "text-amber-600" : "text-gray-500"} />
                  <span>Video</span>
                  {video && (
                    <span className="text-[10px] bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded-full font-bold leading-none">
                      1/1
                    </span>
                  )}
                </button>

                {hoveredTooltip === "video" && (
                  <div className="absolute left-0 bottom-full mb-2 z-50 w-52 p-2.5 bg-gray-900 text-white rounded-xl shadow-2xl text-xs space-y-1.5 border border-gray-700/60 pointer-events-none animate-in fade-in zoom-in-95">
                    <div className="flex items-center gap-1.5 font-bold text-amber-300 text-xs border-b border-gray-700 pb-1">
                      <Video size={13} />
                      <span>Allowed Video Formats</span>
                    </div>
                    <div className="space-y-1 text-[11px] text-gray-200 pt-0.5">
                      <p><span className="font-semibold text-white">• Supported: </span>MP4, MOV, WebM</p>
                      <p><span className="font-semibold text-white">• Limit: </span>Max 1 video</p>
                      <p><span className="font-semibold text-white">• Max Size: </span>200 MB</p>
                    </div>
                    {video ? (
                      <div className="mt-1 pt-1 border-t border-gray-700 text-amber-300 text-[10px] font-medium">
                        ⚠️ 1 video attached. Delete above to replace.
                      </div>
                    ) : (
                      <div className="mt-1 pt-1 border-t border-gray-700 text-gray-300 text-[10px] font-medium">
                        Attached: 0/1
                      </div>
                    )}
                  </div>
                )}
              </div>
              <input
                ref={videoInputRef}
                type="file"
                accept=".mp4,.mov,.webm"
                className="hidden"
                onChange={handleVideo}
              />

              {/* Document / Report Upload Button with Tooltip */}
              <div className="relative inline-block shrink-0">
                <button
                  type="button"
                  onClick={() => docInputRef.current?.click()}
                  onMouseEnter={() => setHoveredTooltip("report")}
                  onMouseLeave={() => setHoveredTooltip(null)}
                  disabled={!!reportFile}
                  className={`flex items-center gap-1.5 px-2 sm:px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${reportFile
                      ? "bg-blue-50/70 text-blue-800 border-blue-300 hover:bg-blue-100"
                      : "border-gray-200 text-gray-600 hover:border-indigo-300 hover:text-indigo-600 bg-white"
                    } disabled:opacity-40 disabled:cursor-not-allowed`}
                >
                  <Paperclip size={14} className={reportFile ? "text-blue-600" : "text-gray-500"} />
                  <span>Report</span>
                  {reportFile && (
                    <span className="text-[10px] bg-blue-200 text-blue-900 px-1.5 py-0.5 rounded-full font-bold leading-none">
                      1/1
                    </span>
                  )}
                </button>

                {hoveredTooltip === "report" && (
                  <div className="absolute left-0 bottom-full mb-2 z-50 w-52 p-2.5 bg-gray-900 text-white rounded-xl shadow-2xl text-xs space-y-1.5 border border-gray-700/60 pointer-events-none animate-in fade-in zoom-in-95">
                    <div className="flex items-center gap-1.5 font-bold text-sky-300 text-xs border-b border-gray-700 pb-1">
                      <Paperclip size={13} />
                      <span>Allowed Report Formats</span>
                    </div>
                    <div className="space-y-1 text-[11px] text-gray-200 pt-0.5">
                      <p><span className="font-semibold text-white">• Supported: </span>PDF, DOC, DOCX</p>
                      <p><span className="font-semibold text-white">• Limit: </span>Max 1 report</p>
                      <p><span className="font-semibold text-white">• Max Size: </span>20 MB</p>
                    </div>
                    {reportFile ? (
                      <div className="mt-1 pt-1 border-t border-gray-700 text-sky-300 text-[10px] font-medium">
                        ⚠️ 1 report attached. Delete above to replace.
                      </div>
                    ) : (
                      <div className="mt-1 pt-1 border-t border-gray-700 text-gray-300 text-[10px] font-medium">
                        Attached: 0/1
                      </div>
                    )}
                  </div>
                )}
              </div>
              <input
                ref={docInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.txt"
                className="hidden"
                onChange={handleDocument}
              />

              {/* Species */}
              <div className="shrink-0">
                <CustomDropdown
                  options={SPECIES_OPTIONS}
                  value={species}
                  onChange={(s) => setSpecies(s)}
                  placeholder="Species"
                  dropUp={true}
                  align="right"
                  buttonClassName="px-2 sm:px-3"
                />
              </div>
            </div>

            {/* Visibility — desktop only: sits to the right of Species in the toolbar row */}
            <div className="hidden sm:block shrink-0">
              <CustomDropdown
                options={[
                  { value: "public", label: "Public" },
                  { value: "private", label: "Private" },
                ]}
                value={isRestricted ? "private" : "public"}
                onChange={(val) => setIsRestricted(val === "private")}
                placeholder="Visibility"
                dropUp={true}
                align="right"
              />
            </div>
          </div>

          {/* Footer Navigation Buttons */}
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={editMode ? (onClose || onBack) : onBack}
              className="text-sm font-semibold text-gray-500 hover:text-gray-700 transition-colors"
            >
              Cancel
            </button>

            <div className="flex items-center gap-2">
              {/* Visibility — mobile only: stays in footer row */}
              <div className="sm:hidden">
                <CustomDropdown
                  options={[
                    { value: "public", label: "Public" },
                    { value: "private", label: "Private" },
                  ]}
                  value={isRestricted ? "private" : "public"}
                  onChange={(val) => setIsRestricted(val === "private")}
                  placeholder="Visibility"
                  dropUp={true}
                  align="right"
                />
              </div>

              <button
                type="button"
                onClick={handleGoToPreview}
                disabled={anyUploading || !hasContent || !title.trim()}
                className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-5 py-2 bg-[#6C63FF] hover:bg-[#5850E6] text-white text-xs sm:text-sm font-semibold rounded-xl transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
              >
                {anyUploading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Uploading…
                  </>
                ) : (
                  <>
                    Preview Discussion
                    <ChevronRight size={16} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickDiscussionComposer;
